import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, Button, ProjectWorkflowStatusBadge } from '../components';
import { ProjectsListTable, type ProjectsListColumn } from '../components/ProjectsListTable';
import { MobileCreateProjectButton } from '../components/MobileCreateProjectButton';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { Breadcrumb } from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import { useAdyenOnboardingPrompt } from '../hooks/useAdyenOnboardingPrompt';
import { adyenOnboardingApi, projectApi, contractApi, milestoneApi, type AdyenAccount } from '../services/api';
import { isPayableContract, projectIdsWithPendingAttestation } from '../utils/pendingAttestation';
import { loadGlobalTemplate, projectDisplayStatusOptions, getStatusLabel as getStatusLabelUtil, type StatusStep } from '../services/projectStatusUtils';
import { projectDisplayStatus } from '../utils/milestoneWorkflowStatus';
import type { Project, Contract } from '../types';
import { useListSort } from '../hooks/useListSort';
import { DEFAULT_PROJECT_LIST_SORT, isCompletedProject, lastActivityColumn, projectListMobileSort } from '../utils/projectListSort';
import { formatLastActivity } from '../utils/lastActivity';
import { ProjectCustomerStatusBadge, projectCustomerState } from '../components/ProjectCustomer';
import { FEATURE_PROJECT_WITHOUT_CUSTOMER } from '../constants/featureFlags';
import CompanyTodoList from '../components/CompanyTodoList';
import SectionPanel from '../components/SectionPanel';
import { useProjectCustomerTodos } from '../hooks/useProjectCustomerTodos';

type CustomerFilter = 'all' | 'missing' | 'waiting';

type SortField = 'projectNumber' | 'description' | 'totalContractAmount' | 'startDate' | 'endDate' | 'status' | 'externalProjectNumber' | 'paidAmount' | 'futurePayments' | 'newMessages' | 'customerName' | 'companyNumber' | 'companyName' | 'lastActivityAt';

const DEFAULT_COLUMN_ORDER: SortField[] = [
  'projectNumber', 'description', 'totalContractAmount', 'customerName',
  'startDate', 'endDate', 'status', 'externalProjectNumber',
  'paidAmount', 'futurePayments', 'newMessages', 'lastActivityAt'
];

const ADMIN_EXTRA_COLUMNS: SortField[] = ['companyNumber', 'companyName'];

const capabilityIsValid = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const capability = value as { allowed?: unknown; verificationStatus?: unknown; status?: unknown };
  return capability.allowed === true || capability.verificationStatus === 'valid' || capability.status === 'valid';
};

const hasLocalMockAdyenId = (value: string | null | undefined, prefix: string): boolean =>
  value ? new RegExp(`^${prefix}-[0-9a-f-]{20,}$`, 'i').test(value) : false;

const adyenAccountAllowsProjectCreation = (account: AdyenAccount | null): boolean => {
  if (!account) return false;
  if (
    hasLocalMockAdyenId(account.adyenAccountHolderId, 'AH') ||
    hasLocalMockAdyenId(account.adyenLegalEntityId, 'LE') ||
    hasLocalMockAdyenId(account.adyenBalanceAccountId, 'BA') ||
    hasLocalMockAdyenId(account.transferInstrumentId, 'SE')
  ) {
    return false;
  }
  const required = account.requiredCapabilities ?? [];
  return (
    account.payoutsAllowed === true &&
    required.length > 0 &&
    required.every((capability) => capabilityIsValid(account.capabilities?.[capability]))
  );
};

/** The project list, or with `completed` the list under "Avslutade Projekt":
 *  the same table with only the completed projects (ClickUp 869fc31mm). */
export function ProjectList({ completed = false }: { completed?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showAdyenOnboardingPrompt = useAdyenOnboardingPrompt(!user?.isAdmin);
  const basePath = user?.isAdmin ? '/admin/projects' : '/projects';
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Special toggle filter — when true, only projects with a non-zero
  // futurePayments amount are shown. Driven by `?pendingPayments=true` so
  // the KPI "Visa väntande insättningar" CTA can deep-link here.
  const [pendingPaymentsOnly, setPendingPaymentsOnly] = useState<boolean>(() => searchParams.get('pendingPayments') === 'true');
  // Alla / Beställare saknas / Väntar på beställaren (ClickUp 869ff1egy),
  // seeded from `?customer=missing|waiting`.
  const [customerFilter, setCustomerFilter] = useState<CustomerFilter>(() => {
    const value = searchParams.get('customer');
    return value === 'missing' || value === 'waiting' ? value : 'all';
  });
  // Filter to projects that have at least one milestone awaiting attestation.
  // Driven by `?pendingAttestations=true` from the dashboard KPI's "Visa" CTA
  // when there is more than one such project. `attestProjectIds` is null while
  // the milestone data needed to compute the set is still loading.
  const [pendingAttestationsOnly, setPendingAttestationsOnly] = useState<boolean>(() => searchParams.get('pendingAttestations') === 'true');
  const [attestProjectIds, setAttestProjectIds] = useState<string[] | null>(null);
  // Filter to projects that have at least one contract awaiting company review.
  // Driven by `?pendingReview=true` from the dashboard "Kontrakt att granska"
  // KPI's "Visa" CTA. `reviewProjectIds` is null while the pending-review data
  // needed to compute the set is still loading.
  const [pendingReviewOnly, setPendingReviewOnly] = useState<boolean>(() => searchParams.get('pendingReview') === 'true');
  const [reviewProjectIds, setReviewProjectIds] = useState<string[] | null>(null);
  const [templateSteps, setTemplateSteps] = useState<StatusStep[]>([]);
  // The project list has no "Avslutat" option: those projects are listed
  // under "Avslutade Projekt" only.
  const statusOptions = useMemo(
    () => projectDisplayStatusOptions(templateSteps).filter(option => option.value !== 'completed'),
    [templateSteps]
  );
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [adyenAccount, setAdyenAccount] = useState<AdyenAccount | null>(null);
  const [isLoadingAdyen, setIsLoadingAdyen] = useState(!user?.isAdmin);
  const adyenReadyForPayments = user?.isAdmin ? true : adyenAccountAllowsProjectCreation(adyenAccount);

  const getDefaultOrder = useCallback(() => user?.isAdmin
    ? ['projectNumber', ...ADMIN_EXTRA_COLUMNS, ...DEFAULT_COLUMN_ORDER.slice(1)] as SortField[]
    : DEFAULT_COLUMN_ORDER, [user?.isAdmin]);

  const [columnOrder, handleColumnReorder] = useColumnOrder(
    user?.isAdmin ? 'projectListColumnOrderAdmin' : 'projectListColumnOrder',
    getDefaultOrder()
  ) as [SortField[], (order: SortField[]) => void];
  const [listSort, handleListSort] = useListSort(
    user?.isAdmin ? 'projectListSortAdmin' : 'projectListSort',
    DEFAULT_PROJECT_LIST_SORT,
    getDefaultOrder()
  );

  const loadData = useCallback(async () => {
    try {
      const [projectsData, contractsData] = await Promise.all([
        projectApi.getProjects(),
        contractApi.getAllContracts()
      ]);
      setProjects(projectsData);
      setContracts(contractsData);
      if (!user?.isAdmin) {
        const account = await adyenOnboardingApi.getStatus();
        setAdyenAccount(account);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingAdyen(false);
    }
  }, [user?.isAdmin]);

  useEffect(() => {
    loadData();
    loadGlobalTemplate().then(setTemplateSteps).catch(() => {});
  }, [loadData]);

  // When filtering on pending attestations, load milestones for the payable
  // contracts (only those can have a funded milestone) and derive the set of
  // projects that have a milestone awaiting attestation. Runs only while the
  // filter is active so the normal list stays a single request.
  useEffect(() => {
    if (!pendingAttestationsOnly) {
      setAttestProjectIds(null);
      return;
    }
    if (contracts.length === 0) {
      setAttestProjectIds([]);
      return;
    }
    let cancelled = false;
    const payableContracts = contracts.filter(isPayableContract);
    Promise.all(
      payableContracts.map(c =>
        milestoneApi.getMilestones(c.id).catch(() => [])
      )
    ).then(results => {
      if (cancelled) return;
      const allMilestones = results.flat();
      setAttestProjectIds(projectIdsWithPendingAttestation(contracts, allMilestones));
    });
    return () => { cancelled = true; };
  }, [pendingAttestationsOnly, contracts]);

  // When filtering on contracts awaiting company review, ask the backend for the
  // same "pending review" set the dashboard KPI counts and derive the projects
  // those contracts belong to. Runs only while the filter is active.
  useEffect(() => {
    if (!pendingReviewOnly) {
      setReviewProjectIds(null);
      return;
    }
    let cancelled = false;
    contractApi.getPendingReview()
      .then(pending => {
        if (cancelled) return;
        setReviewProjectIds(Array.from(new Set(pending.map(p => p.projectId))));
      })
      .catch(() => { if (!cancelled) setReviewProjectIds([]); });
    return () => { cancelled = true; };
  }, [pendingReviewOnly]);

  const getDisplayValue = (value: string | number | undefined, isNumber = false): string => {
    if (isNumber) {
      return value !== undefined && value !== null ? String(value) : '0';
    }
    return value && String(value).trim() !== '' ? String(value) : t('projects.notSpecified');
  };

  const getStatusLabel = (status: string): string => {
    return getStatusLabelUtil(status, templateSteps) || t('projects.notSpecified');
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return t('projects.notSpecified');
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('sv-SE');
    } catch {
      return t('projects.notSpecified');
    }
  };

  const formatNumber = (value: string | number | undefined): string => {
    if (value === undefined || value === null || value === '') return t('projects.notSpecified');
    const num = typeof value === 'string' ? parseFloat(value.replace(/\s/g, '').replace(',', '.')) : value;
    if (isNaN(num)) return t('projects.notSpecified');
    return num.toLocaleString('sv-SE');
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  // Calculate total contract amount per project
  const projectContractTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    contracts.forEach(contract => {
      const amount = parseFloat(contract.contractAmount?.replace(/[^0-9.-]/g, '') || '0');
      if (!isNaN(amount)) {
        totals[contract.projectId] = (totals[contract.projectId] || 0) + amount;
      }
    });
    return totals;
  }, [contracts]);

  const exportToExcel = () => {
    // Create CSV content
    const headers = [
      t('projects.columns.projectNumber'),
      t('projects.columns.description'),
      t('projects.columns.customerName'),
      t('projects.columns.startDate'),
      t('projects.columns.endDate'),
      t('projects.columns.status'),
      t('projects.columns.externalProjectNumber'),
      t('projects.columns.paidAmount'),
      t('projects.columns.futurePayments'),
      t('projects.columns.newMessages')
    ];

    const rows = visibleRows.map(project => [
      project.projectNumber,
      project.description,
      project.customerName || '',
      project.startDate || '',
      project.endDate || '',
      getStatusLabel(projectDisplayStatus(project)),
      project.externalProjectNumber || '',
      project.paidAmount,
      project.futurePayments,
      project.newMessages
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    // Add BOM for Excel to recognize UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `projects_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  const handleRowClick = (projectId: string) => {
    setSelectedProjectId(prev => prev === projectId ? null : projectId);
  };

  const handleEditSelected = () => {
    if (selectedProject) {
      navigate(`${basePath}/${selectedProject.id}`);
    }
  };

  const [visibleRows, setVisibleRows] = useState<Project[]>([]);

  const customerColumnText = (project: Project) => {
    const state = projectCustomerState(project);
    return state === 'linked' ? (project.customerName ?? '') : t(`projectCustomer.status.${state}`);
  };

  const projectListColumns: ProjectsListColumn<Project>[] = useMemo(() => [
    // Project numbers are per company: admin sorts them within each company.
    { field: 'projectNumber', label: t('projects.columns.projectNumber'), ...(user?.isAdmin ? { sortValue: p => p.companyNumber, thenBy: p => p.projectNumber } : {}), render: p => getDisplayValue((p as any).projectNumber) },
    { field: 'companyNumber', label: t('projects.columnCompanyNumber'), thenBy: p => p.projectNumber, render: p => getDisplayValue((p as any).companyNumber) },
    { field: 'companyName', label: t('projects.columnCompanyName'), thenBy: p => p.projectNumber, render: p => getDisplayValue((p as any).companyName) },
    { field: 'description', label: t('projects.columns.description'), render: p => getDisplayValue((p as any).description) },
    { field: 'totalContractAmount', label: t('projects.columns.totalContractAmount'), filter: 'none', sortValue: p => projectContractTotals[p.id] || 0, render: p => formatCurrency(projectContractTotals[p.id] || 0) },
    {
      field: 'customerName',
      label: t('projects.columns.customerName'),
      // A project without a Beställare is marked, not blank (ClickUp 869ff1egy).
      filterValue: p => customerColumnText(p),
      sortValue: p => customerColumnText(p),
      render: p => projectCustomerState(p) === 'linked'
        ? getDisplayValue((p as any).customerName)
        : <ProjectCustomerStatusBadge state={projectCustomerState(p)} />
    },
    { field: 'startDate', label: t('projects.columns.startDate'), sortValue: p => (p.startDate ? new Date(p.startDate).getTime() : 0), render: p => formatDate(p.startDate) },
    { field: 'endDate', label: t('projects.columns.endDate'), sortValue: p => (p.endDate ? new Date(p.endDate).getTime() : 0), render: p => formatDate(p.endDate) },
    { field: 'status', label: t('projects.columns.status'), filter: completed ? 'none' : 'select', selectAllLabel: t('projects.allStatuses'), selectOptions: statusOptions, filterValue: p => projectDisplayStatus(p), sortValue: p => projectDisplayStatus(p), render: p => <ProjectWorkflowStatusBadge project={p} steps={templateSteps} /> },
    { field: 'externalProjectNumber', label: t('projects.columns.externalProjectNumber'), render: p => getDisplayValue((p as any).externalProjectNumber) },
    { field: 'paidAmount', label: t('projects.columns.paidAmount'), filter: 'none', sortValue: p => (p as any).paidAmount || 0, render: p => formatNumber((p as any).paidAmount) },
    { field: 'futurePayments', label: t('projects.columns.futurePayments'), filter: 'none', sortValue: p => (p as any).futurePayments || 0, render: p => formatNumber((p as any).futurePayments) },
    { field: 'newMessages', label: t('projects.columns.newMessages'), filter: 'none', render: p => getDisplayValue((p as any).newMessages) },
    lastActivityColumn<Project>(t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [projectContractTotals, statusOptions, t]);

  // Projects with an actual pending inbetalning: a signed (payable) contract
  // that still has money awaited. Mirrors the "Väntar på insättning" KPI
  // (isPayableContract + remainingAmount) rather than futurePayments, which is
  // the remaining value of ANY contract — including drafts not yet due.
  const pendingPaymentProjectIds = useMemo(
    () => new Set(
      contracts
        .filter(c => isPayableContract(c) && (c.remainingAmount || 0) > 0)
        .map(c => c.projectId)
    ),
    [contracts]
  );

  const listedProjects = useMemo(() => projects.filter(p => isCompletedProject(p) === completed), [projects, completed]);
  const customerCounts = useMemo(() => ({
    all: listedProjects.length,
    missing: listedProjects.filter(p => projectCustomerState(p) === 'missing').length,
    waiting: listedProjects.filter(p => projectCustomerState(p) === 'waiting').length
  }), [listedProjects]);

  const rowsForTable = useMemo(() => {
    let result = listedProjects;
    if (customerFilter !== 'all') result = result.filter(p => projectCustomerState(p) === customerFilter);
    if (pendingPaymentsOnly) result = result.filter(p => pendingPaymentProjectIds.has(p.id));
    if (pendingAttestationsOnly && attestProjectIds) {
      const set = new Set(attestProjectIds);
      result = result.filter(p => set.has(p.id));
    }
    if (pendingReviewOnly && reviewProjectIds) {
      const set = new Set(reviewProjectIds);
      result = result.filter(p => set.has(p.id));
    }
    return result;
  }, [listedProjects, customerFilter, pendingPaymentsOnly, pendingPaymentProjectIds, pendingAttestationsOnly, attestProjectIds, pendingReviewOnly, reviewProjectIds]);

  const initialTableFilters = useMemo<Record<string, string>>(() => {
    const status = searchParams.get('status');
    const init: Record<string, string> = {};
    if (status) init.status = status;
    return init;
  }, [searchParams]);

  const renderProjectMobileCard = (project: Project, { open, selected }: { open: () => void; selected: boolean }) => (
    <div className={`card mb-2 ${selected ? 'border-primary' : ''}`} onClick={() => handleRowClick(project.id)} onDoubleClick={open} style={{ cursor: 'pointer' }}>
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-semibold text-truncate">#{project.projectNumber} {project.description}</span>
              <ProjectWorkflowStatusBadge project={project} steps={templateSteps} className="flex-shrink-0" />
            </div>
            {user?.isAdmin && project.companyName && (
              <div className="text-muted small"><i className="feather-briefcase me-1"></i>{project.companyName}</div>
            )}
            {projectCustomerState(project) !== 'linked' ? (
              <div className="small"><ProjectCustomerStatusBadge state={projectCustomerState(project)} /></div>
            ) : (project as any).customerName && (
              <div className="text-muted small"><i className="feather-user me-1"></i>{(project as any).customerName}</div>
            )}
            <div className="d-flex flex-wrap gap-3 mt-1">
              <span className="text-muted small"><i className="feather-calendar me-1"></i>{formatDate(project.startDate)} - {formatDate(project.endDate)}</span>
              <span className="text-muted small"><i className="feather-tag me-1"></i>{formatCurrency(projectContractTotals[project.id] || 0)}</span>
              <span className="text-muted small"><i className="feather-clock me-1" aria-hidden="true"></i>{t('projects.columns.lastActivityAt')}: {formatLastActivity(project.lastActivityAt, t)}</span>
            </div>
          </div>
          <div className="d-flex gap-1 flex-shrink-0 ms-2">
            <button className="btn btn-sm btn-light-primary" onClick={(e) => { e.stopPropagation(); open(); }} title={t('projects.goToProject', 'Gå till Projekt')}>
              <i className="feather-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // The contractor's own list only, not Admin's or "Avslutade Projekt".
  const customerTodoRows = useProjectCustomerTodos(
    user?.isAdmin || completed ? [] : projects,
    () => void loadData()
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {!completed && !user?.isAdmin && <MobileCreateProjectButton />}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{completed ? t('nav.completedProjects') : t('projects.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: completed ? t('nav.completedProjects') : t('projects.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{completed ? t('projects.completed.description') : t('projects.description')}</p>
        </div>
      </div>

      {pendingPaymentsOnly && (
        <div className="alert alert-info d-flex align-items-center justify-content-between mb-4">
          <div>
            <i className="feather-filter me-2"></i>
            <span className="fw-semibold">{t('projects.pendingPaymentsFilterActive')}</span>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setPendingPaymentsOnly(false);
              navigate('/projects', { replace: true });
            }}
          >
            <i className="feather-x me-1"></i>
            {t('projects.clearFilter')}
          </button>
        </div>
      )}

      {pendingAttestationsOnly && (
        <div className="alert alert-info d-flex align-items-center justify-content-between mb-4">
          <div>
            <i className="feather-filter me-2"></i>
            <span className="fw-semibold">{t('projects.pendingAttestationsFilterActive')}</span>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setPendingAttestationsOnly(false);
              navigate('/projects', { replace: true });
            }}
          >
            <i className="feather-x me-1"></i>
            {t('projects.clearFilter')}
          </button>
        </div>
      )}

      {pendingReviewOnly && (
        <div className="alert alert-info d-flex align-items-center justify-content-between mb-4">
          <div>
            <i className="feather-filter me-2"></i>
            <span className="fw-semibold">{t('projects.pendingReviewFilterActive')}</span>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setPendingReviewOnly(false);
              navigate('/projects', { replace: true });
            }}
          >
            <i className="feather-x me-1"></i>
            {t('projects.clearFilter')}
          </button>
        </div>
      )}

      {showAdyenOnboardingPrompt && !isLoadingAdyen && !adyenReadyForPayments && (
        <div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div>
            <div className="fw-semibold">
              <i className="feather-alert-triangle me-2"></i>
              {t('projects.kycReminderTitle')}
            </div>
            <div className="small">
              {t('projects.kycReminderBody')}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-warning"
            onClick={() => navigate('/settings/company?tab=adyen')}
          >
            {t('projects.kycReminderButton')}
          </button>
        </div>
      )}

      {/* "Att göra nu" for projects without a Beställare, as on Hem (ClickUp 869fmxjgt). */}
      {customerTodoRows.length > 0 && (
        <div className="mb-4" data-testid="project-list-todo">
          <SectionPanel
            id="project-list-todo"
            title={t('home.todoNow.title')}
            icon="clock"
            headerAction={
              <span className="badge bg-danger flex-shrink-0">
                {t('home.todoNow.count', { count: customerTodoRows.length })}
              </span>
            }
          >
            <CompanyTodoList items={customerTodoRows} />
          </SectionPanel>
        </div>
      )}

      <Card>
        <ProjectsListTable
          testIds={{ table: 'project-list-table', cards: 'project-list-cards' }}
          rows={rowsForTable}
          columns={projectListColumns}
          rowKey={p => p.id}
          columnOrder={columnOrder}
          onColumnReorder={(order) => handleColumnReorder(order as SortField[])}
          reorderTitle={t('projects.dragToReorder')}
          sort={listSort}
          onSortChange={handleListSort}
          initialFilters={initialTableFilters}
          onOpen={p => navigate(`${basePath}/${p.id}`)}
          onSelect={p => handleRowClick(p.id)}
          selectedKey={selectedProjectId}
          actionTitle={t('projects.goToProject', 'Gå till Projekt')}
          emptyText={completed ? t('projects.completed.empty') : t('projects.noProjects')}
          noResultsText={t('projects.noResults')}
          mobileFilter={{
            searchField: 'description',
            select: completed ? undefined : { field: 'status', allLabel: t('projects.allStatuses'), options: statusOptions },
            sort: projectListMobileSort(t, !!user?.isAdmin),
          }}
          renderMobileCard={renderProjectMobileCard}
          onVisibleRowsChange={setVisibleRows}
          toolbar={
            <>
              {!completed && (
                <Button onClick={() => navigate(`${basePath}/create`)}>
                  <i className="feather-plus"></i>
                  <span className="d-none d-sm-inline ms-2">{t('projects.createProject')}</span>
                </Button>
              )}
              <Button variant="light-brand" onClick={handleEditSelected} disabled={!selectedProject}>
                <i className="feather-arrow-right"></i>
                <span className="d-none d-sm-inline ms-2">{t('projects.goToProject', 'Gå till Projekt')}</span>
              </Button>
              <Button variant="light-brand" onClick={exportToExcel}>
                <i className="feather-download"></i>
                <span className="d-none d-sm-inline ms-2">{t('projects.export')}</span>
              </Button>
              {FEATURE_PROJECT_WITHOUT_CUSTOMER && !completed && (
                <div className="project-customer-filters" role="group" aria-label={t('projectCustomer.list.filterLabel')} data-testid="project-customer-filters">
                  {(['all', 'missing', 'waiting'] as const).map(key => (
                    <button
                      key={key}
                      type="button"
                      className={`btn ${customerFilter === key ? 'btn-primary' : 'btn-outline-primary'}`}
                      aria-pressed={customerFilter === key}
                      onClick={() => setCustomerFilter(key)}
                      data-testid={`project-customer-filter-${key}`}
                    >
                      {t(`projectCustomer.list.filter.${key}`, { count: customerCounts[key] })}
                    </button>
                  ))}
                </div>
              )}
              {completed && (
                <span className="projects-list-fixed-filter" data-testid="completed-projects-filter">
                  <i className="feather-check-circle" aria-hidden="true"></i>
                  {t('projects.completed.fixedFilter')}
                </span>
              )}
            </>
          }
        />
      </Card>
    </DashboardLayout>
  );
}
